import { useEffect, useRef } from 'react';

function useScrollAnimation(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('visible');
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
}

type TimeSlot = '0〜6時' | '6〜12時' | '12〜18時' | '18〜24時';

const timeSlots: { label: string; key: TimeSlot; sub: string }[] = [
  { label: '6〜12', sub: '時', key: '6〜12時' },
  { label: '12〜18', sub: '時', key: '12〜18時' },
  { label: '18〜24', sub: '時', key: '18〜24時' },
  { label: '0〜6', sub: '時', key: '0〜6時' },
];

const priceData: { day: string; daySub?: string; prices: Record<TimeSlot, number>; highlight?: boolean }[] = [
  {
    day: '月〜木',
    prices: { '0〜6時': 2200, '6〜12時': 550, '12〜18時': 1650, '18〜24時': 2200 },
  },
  {
    day: '金',
    prices: { '0〜6時': 2200, '6〜12時': 550, '12〜18時': 1650, '18〜24時': 2500 },
  },
  {
    day: '土・日',
    daySub: '・祝',
    prices: { '0〜6時': 2200, '6〜12時': 1650, '12〜18時': 2500, '18〜24時': 2500 },
    highlight: true,
  },
];

function getPriceColor(price: number): string {
  if (price >= 2500) return 'text-[#d4af37] font-bold';
  if (price >= 2200) return 'text-[#c8a84b] font-semibold';
  return 'text-white/80';
}

export default function Pricing() {
  const titleRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);

  useScrollAnimation(titleRef as React.RefObject<HTMLElement>);
  useScrollAnimation(tableRef as React.RefObject<HTMLElement>);

  return (
    <section id="pricing" className="bg-[#08090d] py-20 md:py-28">
      <div className="max-w-4xl mx-auto px-3 sm:px-6">
        {/* Title */}
        <div ref={titleRef} className="fade-in-section text-center mb-14">
          <p className="section-label mb-3">PRICING</p>
          <h2 className="font-playfair font-bold text-white text-3xl md:text-4xl mb-4">
            料金
          </h2>
          <p className="text-white/50 font-noto text-sm md:text-base max-w-md mx-auto">
            時間帯・曜日によって変わるシンプルな料金体系。すべて
            <span className="text-[#d4af37] font-semibold">1卓あたり / 1時間</span>の料金です<span className="text-[#d4af37]">（税込）</span>。
          </p>
        </div>

        {/* Price Table */}
        <div ref={tableRef} className="fade-in-section">
          <div className="rounded-2xl overflow-hidden border border-[#2a2f3e]">

            {/* Header row */}
            <div className="grid grid-cols-5 bg-[#1a1f2e]">
              {/* Corner cell */}
              <div className="flex items-center justify-center border-r border-[#2a2f3e] py-3 px-1">
                <span className="text-white/30 text-[10px] font-noto text-center leading-tight">
                  曜日<br />\ 時間
                </span>
              </div>
              {/* Time slot headers */}
              {timeSlots.map((slot) => (
                <div
                  key={slot.key}
                  className="flex flex-col items-center justify-center border-r border-[#2a2f3e] last:border-r-0 py-3 px-1"
                >
                  <span className="text-white/70 font-noto font-semibold text-center leading-none text-[11px] sm:text-xs md:text-sm">
                    {slot.label}
                  </span>
                  <span className="text-white/40 font-noto text-[10px] sm:text-xs mt-0.5">
                    {slot.sub}
                  </span>
                </div>
              ))}
            </div>

            {/* Data rows */}
            {priceData.map((row) => (
              <div
                key={row.day}
                className={`grid grid-cols-5 border-t border-[#2a2f3e] transition-colors hover:bg-white/5
                  ${row.highlight ? 'bg-[#d4af37]/5' : 'bg-[#0f1118]'}`}
              >
                {/* Day label */}
                <div
                  className={`flex flex-col items-center justify-center border-r border-[#2a2f3e] py-4 px-1
                    ${row.highlight ? 'bg-[#d4af37]/10' : ''}`}
                >
                  <span
                    className={`font-noto font-bold text-center leading-tight text-[11px] sm:text-sm md:text-base
                      ${row.highlight ? 'text-[#d4af37]' : 'text-white/80'}`}
                  >
                    {row.day}
                  </span>
                  {row.daySub && (
                    <span
                      className={`font-noto font-bold text-[10px] sm:text-xs leading-tight
                        ${row.highlight ? 'text-[#d4af37]' : 'text-white/60'}`}
                    >
                      {row.daySub}
                    </span>
                  )}
                </div>

                {/* Price cells */}
                {timeSlots.map((slot) => {
                  const price = row.prices[slot.key];
                  const isHighest = price >= 2500;
                  return (
                    <div
                      key={slot.key}
                      className={`flex flex-col items-center justify-center border-r border-[#2a2f3e] last:border-r-0 py-4 px-1
                        ${isHighest ? 'bg-[#d4af37]/8' : ''}`}
                    >
                      <span className={`font-playfair text-sm sm:text-base md:text-xl leading-none ${getPriceColor(price)}`}>
                        ¥{price.toLocaleString()}
                      </span>
                      <span className="text-white/30 text-[10px] font-noto mt-1">税込 / h</span>
                    </div>
                  );
                })}
              </div>
            ))}

            {/* Footer note */}
            <div className="bg-[#1a1f2e] px-4 py-4 border-t border-[#2a2f3e]">
              <p className="text-white/40 text-xs font-noto text-center tracking-wide">
                ※ 料金はすべて1卓あたり・1時間の金額です<span className="text-[#d4af37]">（税込）</span>。最大6名/1卓。
              </p>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3 md:gap-6">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-white/20 inline-block shrink-0" />
              <span className="text-white/40 text-xs font-noto">¥550〜1,650<span className="text-[#d4af37]">税込</span>（デイタイム）</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#c8a84b] inline-block shrink-0" />
              <span className="text-white/40 text-xs font-noto">¥2,200<span className="text-[#d4af37]">税込</span>（ナイト）</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#d4af37] inline-block shrink-0" />
              <span className="text-white/40 text-xs font-noto">¥2,500<span className="text-[#d4af37]">税込</span>（ピーク）</span>
            </div>
          </div>
        </div>

        {/* Info cards */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { icon: 'ri-group-line', text: '最大6名 / 1卓' },
            { icon: 'ri-time-line', text: '24時間・年中無休' },
            { icon: 'ri-calendar-check-line', text: '当日予約OK' },
          ].map((item) => (
            <div
              key={item.text}
              className="flex items-center gap-3 bg-[#1a1f2e] rounded-xl px-5 py-4 border border-[#2a2f3e]"
            >
              <div className="w-8 h-8 flex items-center justify-center shrink-0">
                <i className={`${item.icon} text-[#d4af37] text-lg`} />
              </div>
              <p className="text-white/60 text-sm font-noto">{item.text}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-10 text-center">
          <a
            href="https://upnow.jp/duel"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-12 py-4 bg-[#d4af37] text-black font-bold text-base rounded-full hover:bg-[#e8c84a] hover:scale-105 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            予約する
          </a>
        </div>
      </div>
    </section>
  );
}
